import { useEffect, useState, type ReactNode } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, type Appearance, type Stripe } from '@stripe/stripe-js'
import { useTheme } from '@/hooks/useTheme'
import { EmptyState } from '@/components/patterns/empty-state'
import { Typography } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import { ADMIN_FONT_FAMILY, getAdminFontSources } from '@/lib/admin-font'
import type { CustomerSavedCard } from '@/lib/services/customers/customers.types'
import {
  SecureCardFormLoadingState,
  StripeElements,
  type CardEntryStage,
  type PaymentModeProps,
  type SetupModeProps,
} from './StripeElements'

/** The Stripe.js configuration card entry loads Stripe with: the platform's publishable key and the shop's account. */
export interface StripeConfiguration {
  publishable_key?: string
  stripe_account?: string | null
}

/**
 * What card entry reads from the payment state it collects for (`PayNowPaymentState`): an order's bootstrap, whose
 * Stripe configuration comes first, the state's Stripe payload, and its currency. A statement's bootstrap carries no
 * Stripe configuration, only its preparation state, so any bootstrap object fits.
 */
export interface CardEntryPaymentState {
  payment_bootstrap?: object & StripeConfiguration
  stripe?: StripeConfiguration
  currency_type?: string | null
}

/** Card entry's progress, as PaymentForm reports it with its Elements tree's key. */
export type CardInitializationStage =
  | 'stripe_js_requested'
  | 'stripe_js_ready'
  | 'stripe_js_failed'
  | 'stripe_elements_mounted'
  | CardEntryStage

/** Where card entry gets its Stripe.js configuration: the bootstrap first, then the payment state. */
interface StripeSource {
  paymentState?: CardEntryPaymentState | null
  paymentBootstrap?: StripeConfiguration | null
  salesDemoMode?: boolean
}

interface PaymentFormSharedProps {
  paymentBootstrap?: StripeConfiguration | null
  disabled: boolean
  onError: (message: string) => void
  submitButtonLabel?: string
  submittingButtonLabel?: string
  /** The version of what staff confirm; a new version asks them again. */
  requiredConfirmationVersion?: string | null
  /** What staff confirm before submitting; no confirmation without one. */
  requiredConfirmationLabel?: string | null
  requiredConfirmationContent?: ReactNode
  requiredConfirmationErrorMessage?: string
  canSavePaymentMethod?: boolean
  requireSavedPaymentMethod?: boolean
  savedPaymentMethods?: CustomerSavedCard[]
  isSavedPaymentMethodsLoading?: boolean
  onInitializationStage?: (stage: CardInitializationStage, initializationKey: string) => void
  salesDemoMode?: boolean
}

/** A form that charges the card, for the payment state it collects for. */
export interface PaymentFormPaymentProps extends PaymentFormSharedProps, PaymentModeProps {
  paymentState: CardEntryPaymentState | null
  /** The amount to charge, in dollars; none while the amount staff entered can't be charged. */
  amount: number | undefined
}

/** A form that saves the card: no payment state or amount. */
export interface PaymentFormSetupProps extends PaymentFormSharedProps, SetupModeProps {
  paymentState?: undefined
  amount?: undefined
}

export type PaymentFormProps = PaymentFormPaymentProps | PaymentFormSetupProps

const stripePromiseCache = new Map<string, Promise<Stripe | null>>()
const stripeElementsFonts = getAdminFontSources()
const MAX_STRIPE_PROMISE_CACHE_ENTRIES = 20
const MINIMUM_CARD_PAYMENT_AMOUNT_IN_CENTS = 50

function getStripeCacheKey(
  publishableKey: string | undefined,
  stripeAccount: string | null | undefined,
  salesDemoMode = false
) {
  if (!publishableKey) {
    return null
  }

  return `${publishableKey}::${stripeAccount ?? ''}::${salesDemoMode ? 'sales-demo' : 'standard'}`
}

function getCachedStripePromise(cacheKey: string | null) {
  if (!cacheKey) {
    return null
  }

  return stripePromiseCache.get(cacheKey) ?? null
}

function touchStripePromiseCache(cacheKey: string | null) {
  const cachedPromise = cacheKey ? stripePromiseCache.get(cacheKey) : undefined
  if (!cacheKey || !cachedPromise) {
    return
  }

  stripePromiseCache.delete(cacheKey)
  stripePromiseCache.set(cacheKey, cachedPromise)
}

function cacheStripePromise(
  publishableKey: string | undefined,
  stripeAccount: string | null | undefined,
  salesDemoMode = false
): Promise<Stripe | null> | null {
  const cacheKey = getStripeCacheKey(publishableKey, stripeAccount, salesDemoMode)
  if (!publishableKey || !cacheKey) {
    return null
  }

  const cachedPromise = stripePromiseCache.get(cacheKey)
  if (cachedPromise) {
    return cachedPromise
  }

  const stripePromise = loadStripe(publishableKey, {
    ...(stripeAccount ? { stripeAccount } : {}),
    ...(salesDemoMode
      ? {
          developerTools: {
            assistant: {
              enabled: false,
            },
          },
        }
      : {}),
  })
  stripePromiseCache.set(cacheKey, stripePromise)

  void stripePromise.then(undefined, () => {
    if (stripePromiseCache.get(cacheKey) === stripePromise) {
      stripePromiseCache.delete(cacheKey)
    }
  })

  if (stripePromiseCache.size > MAX_STRIPE_PROMISE_CACHE_ENTRIES) {
    const oldestCacheKey = stripePromiseCache.keys().next().value
    if (oldestCacheKey) {
      stripePromiseCache.delete(oldestCacheKey)
    }
  }

  return stripePromise
}

function resolveStripeConfiguration(
  paymentState: CardEntryPaymentState | null | undefined,
  paymentBootstrap: StripeConfiguration | null | undefined
) {
  return {
    publishableKey:
      paymentBootstrap?.publishable_key ??
      paymentState?.payment_bootstrap?.publishable_key ??
      paymentState?.stripe?.publishable_key,
    stripeAccount:
      paymentBootstrap?.stripe_account ??
      paymentState?.payment_bootstrap?.stripe_account ??
      paymentState?.stripe?.stripe_account,
  }
}

// Identifies one Stripe Elements tree. The amount isn't part of it: Elements takes a new amount in place
// (react-stripe-js passes changed options to elements.update), so card details staff already typed survive
// an amount change.
export function getPaymentElementsInitializationKey({
  paymentState,
  paymentBootstrap = null,
  confirmationMode = 'payment',
  salesDemoMode = false,
}: StripeSource & { confirmationMode?: 'payment' | 'setup' }): string {
  const { publishableKey, stripeAccount } = resolveStripeConfiguration(
    paymentState,
    paymentBootstrap
  )
  const stripeCacheKey = getStripeCacheKey(publishableKey, stripeAccount, salesDemoMode)
  const currency = String(paymentState?.currency_type ?? 'usd').toLowerCase()

  return `${stripeCacheKey}:${confirmationMode}:${currency}`
}

export function preloadPaymentStripe({
  paymentState,
  paymentBootstrap = null,
  salesDemoMode = false,
}: StripeSource): Promise<Stripe | null> | null {
  const { publishableKey, stripeAccount } = resolveStripeConfiguration(
    paymentState,
    paymentBootstrap
  )

  return cacheStripePromise(publishableKey, stripeAccount, salesDemoMode)
}

export function PaymentForm({
  paymentState,
  paymentBootstrap = null,
  amount,
  disabled,
  onError,
  submitButtonLabel,
  submittingButtonLabel,
  requiredConfirmationVersion = null,
  requiredConfirmationLabel = null,
  requiredConfirmationContent = null,
  requiredConfirmationErrorMessage,
  canSavePaymentMethod = false,
  requireSavedPaymentMethod = false,
  savedPaymentMethods = [],
  isSavedPaymentMethodsLoading = false,
  onInitializationStage = undefined,
  salesDemoMode = false,
  ...cardEntryMode
}: PaymentFormProps) {
  const confirmationMode = cardEntryMode.confirmationMode ?? 'payment'
  const { theme } = useTheme()
  const isDarkMode = theme === 'dark'
  const { publishableKey, stripeAccount } = resolveStripeConfiguration(
    paymentState,
    paymentBootstrap
  )
  const currency = String(paymentState?.currency_type ?? 'usd').toLowerCase()
  const paymentAmount = Math.round(Number(amount) * 100)
  const canInitializePaymentElement =
    Number.isInteger(paymentAmount) && paymentAmount >= MINIMUM_CARD_PAYMENT_AMOUNT_IN_CENTS
  const stripeCacheKey = getStripeCacheKey(publishableKey, stripeAccount, salesDemoMode)
  const stripeElementsKey = getPaymentElementsInitializationKey({
    paymentState,
    paymentBootstrap,
    confirmationMode,
    salesDemoMode,
  })
  const [stripePromiseEntry, setStripePromiseEntry] = useState(() => ({
    cacheKey: stripeCacheKey,
    promise: getCachedStripePromise(stripeCacheKey),
  }))
  const [stripeMountState, setStripeMountState] = useState(() => ({
    cacheKey: stripeCacheKey,
    shouldMount: false,
  }))

  useEffect(() => {
    onInitializationStage?.('stripe_js_requested', stripeElementsKey)

    queueMicrotask(() => {
      const nextStripePromise = stripeCacheKey
        ? cacheStripePromise(publishableKey, stripeAccount, salesDemoMode)
        : null

      if (nextStripePromise) {
        void nextStripePromise.then(
          () => onInitializationStage?.('stripe_js_ready', stripeElementsKey),
          () => onInitializationStage?.('stripe_js_failed', stripeElementsKey)
        )
      }

      setStripePromiseEntry((current) =>
        current.cacheKey === stripeCacheKey && current.promise === nextStripePromise
          ? current
          : { cacheKey: stripeCacheKey, promise: nextStripePromise }
      )
    })
  }, [
    onInitializationStage,
    publishableKey,
    salesDemoMode,
    stripeAccount,
    stripeCacheKey,
    stripeElementsKey,
  ])

  useEffect(() => {
    queueMicrotask(() => {
      setStripeMountState((current) =>
        current.cacheKey === stripeCacheKey && current.shouldMount === true
          ? current
          : { cacheKey: stripeCacheKey, shouldMount: true }
      )
    })
  }, [stripeCacheKey])

  const stripePromise =
    stripePromiseEntry.cacheKey === stripeCacheKey ? stripePromiseEntry.promise : null
  const shouldMountStripe =
    stripeMountState.cacheKey === stripeCacheKey ? stripeMountState.shouldMount : false
  const stripeElementsAppearance: Appearance = {
    theme: theme === 'dark' ? 'night' : 'stripe',
    variables: {
      fontFamily: ADMIN_FONT_FAMILY,
      // Hosted fields cannot inherit our CSS tokens; mirror --placeholder-foreground.
      colorTextPlaceholder: isDarkMode ? '#8e8891' : '#8e8993',
    },
  }

  useEffect(() => {
    if (!stripeCacheKey || !stripePromise || !shouldMountStripe) {
      return
    }

    touchStripePromiseCache(stripeCacheKey)
  }, [shouldMountStripe, stripeCacheKey, stripePromise])

  useEffect(() => {
    if (stripePromise && shouldMountStripe) {
      onInitializationStage?.('stripe_elements_mounted', stripeElementsKey)
    }
  }, [onInitializationStage, shouldMountStripe, stripeElementsKey, stripePromise])

  if (confirmationMode === 'payment' && !canInitializePaymentElement) {
    return (
      <EmptyState
        className="min-h-56"
        size="compact"
        title="Adjust the payment amount to continue with card entry."
      />
    )
  }

  if (!stripePromise || !shouldMountStripe) {
    return (
      <div className="space-y-5 pt-1">
        <div className="space-y-5 rounded-xl bg-muted/25 p-4 ring-1 ring-border/50 dark:bg-white/[0.03] dark:ring-white/8">
          <div className="grid min-h-24">
            <SecureCardFormLoadingState />
          </div>
        </div>
        <div className="pt-1">
          <Button type="button" disabled className="w-full">
            {submitButtonLabel ?? 'Pay Now'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Elements
      key={stripeElementsKey}
      stripe={stripePromise}
      options={
        confirmationMode === 'payment'
          ? {
              mode: 'payment',
              amount: paymentAmount,
              currency,
              paymentMethodTypes: ['card'],
              paymentMethodCreation: 'manual',
              appearance: stripeElementsAppearance,
              fonts: stripeElementsFonts,
            }
          : {
              appearance: stripeElementsAppearance,
              fonts: stripeElementsFonts,
            }
      }
    >
      <StripeElements
        {...cardEntryMode}
        disabled={disabled}
        isDarkMode={isDarkMode}
        onError={onError}
        submitButtonLabel={submitButtonLabel}
        submittingButtonLabel={submittingButtonLabel}
        requiredConfirmationVersion={requiredConfirmationVersion}
        requiredConfirmationLabel={requiredConfirmationLabel}
        requiredConfirmationContent={requiredConfirmationContent}
        requiredConfirmationErrorMessage={requiredConfirmationErrorMessage}
        canSavePaymentMethod={canSavePaymentMethod}
        requireSavedPaymentMethod={requireSavedPaymentMethod}
        savedPaymentMethods={savedPaymentMethods}
        isSavedPaymentMethodsLoading={isSavedPaymentMethodsLoading}
        onInitializationStage={(stage) => onInitializationStage?.(stage, stripeElementsKey)}
        salesDemoMode={salesDemoMode}
      />
    </Elements>
  )
}
